import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { apiFetch } from '../../lib/api'
import type { PlayerPosition } from '../../types'
import FibaBadge from '../profile/FibaBadge'
import Avatar from '../Avatar'

export interface OutreachReceivedItem {
  id: string
  player_id: string
  player_slug: string
  player_name: string
  requester_display_name: string
  // Migration 0017 — public coach-profile slug for the requester when a
  // coaches row exists; null otherwise. Drives the /coaches/:slug link.
  requester_coach_slug: string | null
  message: string
  created_at: string
}

// Claim→Confirm — a representation request this agent has sent.
export interface RepresentationRequestItem {
  id: string
  player_id: string
  player_slug: string
  player_name: string
  status: 'pending' | 'confirmed' | 'rejected'
  created_at: string
  expires_at: string
}

export interface AgentDashboardData {
  agent: {
    id: string
    name: string
    whatsapp: string | null
    is_fiba_licensed: boolean
    fiba_license_number: string | null
    bio: string | null
    photo_url: string | null
  }
  profile: {
    slug: string
    headline: string | null
    years_experience: number | null
  } | null
  agency: { id: string; name: string } | null
  players: Array<{
    id: string
    slug: string
    name: string
    position: PlayerPosition | null
    nationality: string | null
    photo_url: string | null
    updated_at: string
  }>
  // M5.2 — coach outreach received on this agent's roster, newest
  // first. The visible-from-here-on-out signal that closes the M5.1
  // loop: outreach a coach sent on a discovery player before claim
  // sits invisible until the agent claims; then it materializes here.
  outreach_received: OutreachReceivedItem[]
  // Claim→Confirm — representation requests this agent has sent (pending /
  // confirmed / rejected), newest first. Withdraw is available on pending.
  representation_requests: RepresentationRequestItem[]
}

export default function AgentDashboard({ data }: { data: AgentDashboardData }) {
  const { agent, profile, agency, players, outreach_received, representation_requests } = data
  // B12 — one-shot flash notice passed via router state (e.g. PlayerEdit's
  // remove-player success). Cleared from history state on mount so a
  // refresh / back-navigation doesn't replay it.
  const location = useLocation()
  const [flash, setFlash] = useState<string | null>(
    (location.state as { flash?: string } | null)?.flash ?? null,
  )
  useEffect(() => {
    if ((location.state as { flash?: string } | null)?.flash) {
      window.history.replaceState({}, '')
    }
  }, [location.state])
  return (
    <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
      {flash && (
        <div className="flex items-start justify-between gap-3 border border-hoop-green-bg bg-hoop-green-bg text-hoop-green rounded-md px-4 py-3 text-sm">
          <span>{flash}</span>
          <button
            type="button"
            onClick={() => setFlash(null)}
            aria-label="Dismiss notice"
            className="shrink-0 font-medium hover:opacity-70"
          >
            ✕
          </button>
        </div>
      )}
      <ProfileCard agent={agent} profile={profile} agency={agency} />
      <Roster players={players} />
      <RepresentationRequests items={representation_requests} />
      <CoachInterest items={outreach_received} />
    </div>
  )
}

// Claim→Confirm — representation requests this agent has sent. Pending rows
// get a Withdraw action (DELETE /api/players/:id/represent). Confirmed players
// also appear in the Roster above; listing them here too confirms the request
// was accepted. Hidden entirely when the agent has sent none.
function RepresentationRequests({ items }: { items: RepresentationRequestItem[] }) {
  const [withdrawing, setWithdrawing] = useState<string | null>(null)
  const [hidden, setHidden] = useState<Set<string>>(() => new Set())
  const [err, setErr] = useState<string | null>(null)

  async function withdraw(item: RepresentationRequestItem) {
    setWithdrawing(item.id)
    setErr(null)
    try {
      const res = await apiFetch(
        `/api/players/${encodeURIComponent(item.player_id)}/represent`,
        { method: 'DELETE' },
      )
      if (!res.ok) throw new Error(`withdraw_failed_${res.status}`)
      setHidden((prev) => {
        const next = new Set(prev)
        next.add(item.id)
        return next
      })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
    } finally {
      setWithdrawing(null)
    }
  }

  const visible = items.filter((i) => !hidden.has(i.id))
  if (visible.length === 0) return null

  return (
    <section>
      <h2 className="text-lg font-semibold text-hoop-text mb-4">
        Representation requests{' '}
        <span className="text-hoop-text-3 font-normal">· {visible.length}</span>
      </h2>
      {err && <p className="mb-3 text-sm text-hoop-red">{err}</p>}
      <ul className="bg-hoop-surface border border-hoop-border rounded-lg divide-y divide-hoop-border">
        {visible.map((item) => (
          <li
            key={item.id}
            className="flex items-center justify-between gap-3 px-4 py-3 flex-wrap"
          >
            <div className="min-w-0">
              <Link
                to={`/players/${item.player_slug}`}
                className="font-medium text-hoop-text hover:underline"
              >
                {item.player_name}
              </Link>
              <div className="text-xs text-hoop-text-3 mt-0.5">
                Sent {relativeTime(item.created_at)}
                {item.status === 'pending' && ` · expires ${shortDate(item.expires_at)}`}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <RequestStatusBadge status={item.status} />
              {item.status === 'pending' && (
                <button
                  type="button"
                  disabled={withdrawing === item.id}
                  onClick={() => withdraw(item)}
                  className="text-xs text-hoop-text-3 hover:text-hoop-red disabled:opacity-40"
                >
                  {withdrawing === item.id ? 'Withdrawing…' : 'Withdraw'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

function RequestStatusBadge({
  status,
}: {
  status: 'pending' | 'confirmed' | 'rejected'
}) {
  const cls: Record<typeof status, string> = {
    pending: 'bg-hoop-amber-bg text-hoop-amber',
    confirmed: 'bg-hoop-green-bg text-hoop-green',
    rejected: 'bg-hoop-bg text-hoop-text-2',
  }
  const label: Record<typeof status, string> = {
    pending: 'Pending',
    confirmed: 'Confirmed',
    rejected: 'Declined',
  }
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cls[status]}`}
    >
      {label[status]}
    </span>
  )
}

function shortDate(iso: string): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso.slice(0, 10)
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function ProfileCard({
  agent,
  profile,
  agency,
}: {
  agent: AgentDashboardData['agent']
  profile: AgentDashboardData['profile']
  agency: AgentDashboardData['agency']
}) {
  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-lg p-6">
      {/* Stacks below sm — same 360px squeeze as the coach ProfileCard. */}
      <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <Avatar
            src={agent.photo_url}
            name={agent.name}
            className="w-16 h-16 rounded-full border border-hoop-border flex-shrink-0"
            textClassName="text-lg"
          />
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-hoop-text-3 mb-1">
              Agent profile
            </div>
            <h1 className="text-2xl font-bold text-hoop-text truncate">
              {agent.name}
            </h1>
            {profile?.headline && (
              <p className="text-hoop-text-2 mt-1">{profile.headline}</p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-hoop-text-2">
              {agency && (
                <span className="px-2 py-0.5 rounded bg-hoop-bg text-hoop-text-2">
                  {agency.name}
                </span>
              )}
              <FibaBadge licenseNumber={agent.fiba_license_number} />
              {profile?.years_experience != null && (
                <span className="text-hoop-text-3">
                  {profile.years_experience} yr{profile.years_experience === 1 ? '' : 's'} experience
                </span>
              )}
            </div>
            {agent.bio && (
              <p className="mt-4 text-sm text-hoop-text-2 whitespace-pre-line">
                {agent.bio}
              </p>
            )}
          </div>
        </div>
        <Link
          to="/dashboard/agent/edit"
          className="text-sm text-hoop-text-2 hover:text-hoop-text whitespace-nowrap"
        >
          Edit profile →
        </Link>
      </div>
    </section>
  )
}

// M5.2 — Coach interest section. Surfaces outreach_requests rows the
// dashboard endpoint now joins for the calling agent. Pure render —
// the only state lives in the parent payload.
function CoachInterest({ items }: { items: OutreachReceivedItem[] }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-hoop-text">
          Coach interest
          {items.length > 0 && (
            <span className="text-hoop-text-3 font-normal">
              {' '}· {items.length}
            </span>
          )}
        </h2>
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-hoop-border rounded-lg p-10 text-center text-sm text-hoop-text-3">
          No coach interest yet on your players. Once a coach expresses
          interest, it'll show up here.
        </div>
      ) : (
        <ul className="bg-hoop-surface border border-hoop-border rounded-lg divide-y divide-hoop-border">
          {items.map((item) => (
            <li key={item.id} className="px-4 py-3">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <Link
                    to={`/players/${item.player_slug}`}
                    className="font-medium text-hoop-text hover:underline"
                  >
                    {item.player_name}
                  </Link>
                  <span className="text-hoop-text-3 text-sm">
                    {' '}· from{' '}
                  </span>
                  {/* Migration 0017 — link the coach name to their public
                      profile when they have one. Minimal <a>-equivalent. */}
                  {item.requester_coach_slug ? (
                    <Link
                      to={`/coaches/${item.requester_coach_slug}`}
                      className="text-sm text-hoop-text-2 hover:underline"
                    >
                      {item.requester_display_name}
                    </Link>
                  ) : (
                    <span className="text-sm text-hoop-text-2">
                      {item.requester_display_name}
                    </span>
                  )}
                </div>
                <span className="text-xs text-hoop-text-3 whitespace-nowrap">
                  {relativeTime(item.created_at)}
                </span>
              </div>
              <p className="mt-2 text-sm text-hoop-text-2 whitespace-pre-line">
                {item.message}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// "3 days ago" style for the coach-interest list. Falls back to the
// raw ISO when the input doesn't parse, so a malformed value never
// crashes the row render.
function relativeTime(iso: string): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  const diffMs = Date.now() - t
  const sec = Math.max(0, Math.floor(diffMs / 1000))
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} min ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`
  const wk = Math.floor(day / 7)
  if (wk < 5) return `${wk} week${wk === 1 ? '' : 's'} ago`
  return new Date(t).toLocaleDateString()
}

function Roster({ players }: { players: AgentDashboardData['players'] }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-hoop-text">
          Roster{' '}
          <span className="text-hoop-text-3 font-normal">
            · {players.length}
          </span>
        </h2>
        <Link
          to="/dashboard/players/new"
          className="bg-hoop-primary text-hoop-primary-fg text-sm px-4 py-2 rounded-md font-medium hover:opacity-90"
        >
          Add Player
        </Link>
      </div>

      {players.length === 0 ? (
        <div className="border border-dashed border-hoop-border rounded-lg p-10 text-center text-sm text-hoop-text-3">
          No players yet. Add your first player to build out your roster.
        </div>
      ) : (
        <ul className="bg-hoop-surface border border-hoop-border rounded-lg divide-y divide-hoop-border">
          {players.map(p => (
            <li key={p.id} className="flex items-center gap-4 px-4 py-3">
              <Avatar
                src={p.photo_url}
                name={p.name}
                className="w-10 h-10 rounded-md border border-hoop-border flex-shrink-0"
                textClassName="text-xs"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-hoop-text truncate">
                  {p.name}
                </div>
                <div className="text-xs text-hoop-text-3 truncate">
                  {[p.position, p.nationality].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <Link
                to={`/players/${p.slug}`}
                className="text-sm text-hoop-text-2 hover:text-hoop-text"
              >
                View
              </Link>
              <Link
                to={`/dashboard/players/${p.slug}/edit`}
                className="text-sm text-hoop-text-2 hover:text-hoop-text"
              >
                Edit
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
